import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  GitCompare,
  Check,
  Clock,
  FileText,
  Link2,
  Loader2,
  Plus,
  ShieldCheck,
} from "lucide-react";
import { Dropdown, type DropdownOption } from "../../../components/Dropdown";
import {
  AiSummaryCard,
  BarKey,
  OutcomeSection,
  ScopeStat,
  StatusChip,
  TargetScoreCard,
} from "./CatalogCompareParts";
import { DataTable, type Column } from "../../../components/DataTable";
import { DonutPieChart } from "../../../components/ChartsComponent";
import {
  compareCatalogs,
  listCatalogs,
  summarizeCatalogComparison,
  type CatalogComparisonMatch,
  type CatalogComparisonResponse,
  type CatalogComparisonSummaryResponse,
} from "../../../../api/frameworksApi";
import { Tip } from "../../../components/ui/tooltip";

const COVERED_COLOR = "#22c55e";
const GAP_COLOR = "#f59e0b";
const NET_NEW_COLOR = "#7c3aed";
const STATUS_COLOR: Record<string, string> = {
  implemented: COVERED_COLOR,
  planned: "#f59e0b",
  "partially-implemented": "#38bdf8",
  "not-implemented": "#f97316",
  "not-applicable": "#94a3b8",
};
const FALLBACK_STATUS_COLOR = "#94a3b8";

const STATUS_PHRASE: Record<string, string> = {
  "not-implemented": "are not implemented yet",
  planned: "are planned",
  partial: "are partially implemented",
  "partially-implemented": "are partially implemented",
  "not-applicable": "are not applicable",
};


interface GroupedMatchRow {
  targetControlId: string;
  status: string;
  sourceControlIds: string[];
}

function formatStatus(status: string): string {
  return status
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

export function CatalogCompareView({
  sspId,
  sspName,
  sourceLabel,
  onBack,
}: {
  sspId: string;
  sspName?: string;
  /** Source catalog of the SSP - the same title the drawer hero shows. */
  sourceLabel?: string;
  onBack: () => void;
}) {
  const sourceName = sourceLabel || sspName || sspId;
  const [options, setOptions] = useState<DropdownOption[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [catalogId, setCatalogId] = useState("");
  const [result, setResult] = useState<CatalogComparisonResponse | null>(null);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* One outcome section is open at a time; the good news opens by default. */
  const [openSection, setOpenSection] = useState<string | null>("covered");
  const [gapStatus, setGapStatus] = useState<string | null>(null);
  const [aiSummary, setAiSummary] = useState<
    CatalogComparisonSummaryResponse | string | null
  >(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

useEffect(() => {
  let cancelled = false;

  setLoadingCatalogs(true);

  listCatalogs("REGULATORY")
    .then((res) => {
      if (cancelled) return;

      const sourceCatalogId = sspId;
      const sourceCatalogName = sourceLabel || sspName || "";

      setOptions(
        (res?.data ?? [])
          .filter((c) => {
            const catalogId = c?.id ?? "";
            const catalogName = c?.title || c?.documentId || "";

            if (catalogId === sourceCatalogId) {
              return false;
            }

            if (
              sourceCatalogName &&
              catalogName.trim().toLowerCase() ===
                sourceCatalogName.trim().toLowerCase()
            ) {
              return false;
            }

            return true;
          })
          .map((c) => ({
            value: c?.id ?? "",
            label:
              c?.title ||
              c?.documentId ||
              c?.id ||
              "Untitled catalog",
          })),
      );
    })
    .catch(() => {
      if (!cancelled) {
        setOptions([]);
      }
    })
    .finally(() => {
      if (!cancelled) {
        setLoadingCatalogs(false);
      }
    });

  return () => {
    cancelled = true;
  };
}, [sspId, sourceLabel, sspName]);




  const loadAiSummary = async (data: CatalogComparisonResponse) => {
    setAiLoading(true);
    setAiError(null);
    setAiSummary(null);
    try {
      const res = await summarizeCatalogComparison({
        sspId: data.sourceSspId ?? sspId,
        targetCatalogId: data.targetCatalogId ?? catalogId,
        totalSourceControls: data.totalSourceControls ?? 0,
        totalTargetControls: data.totalTargetControls ?? 0,
        totalCommonControls: data.summary?.totalCommonControls ?? 0,
        totalTargetOnlyControls: data.summary?.totalTargetOnlyControls ?? 0,
        implementedCommonControls: data.summary?.implementedCommonControls ?? 0,
        notImplementedCommonControlsByStatus:
          data.summary?.notImplementedCommonControlsByStatus ?? {},
      });
      setAiSummary(res?.data ?? null);
    } catch (err) {
      setAiSummary(null);
      setAiError(
        err instanceof Error
          ? err.message
          : "Could not generate the AI summary.",
      );
    } finally {
      setAiLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!catalogId || comparing) return;
    setComparing(true);
    setError(null);
    setAiSummary(null);
    setAiError(null);
    try {
      const res = await compareCatalogs(sspId, catalogId);
      const data = res?.data ?? null;
      setResult(data);
      setOpenSection("covered");
      setGapStatus(null);
      if (data) void loadAiSummary(data);
    } catch (err) {
      setResult(null);
      setError(
        err instanceof Error
          ? err.message
          : "Could not run the catalog comparison.",
      );
    } finally {
      setComparing(false);
    }
  };

  const summary = result?.summary;

  const catalogLabel =
    options.find((o) => o.value === (result?.targetCatalogId ?? catalogId))
      ?.label ??
    result?.targetCatalogId ??
    "";


  const pendingByStatus = useMemo(
    () => Object.entries(summary?.notImplementedCommonControlsByStatus ?? {}),
    [summary],
  );
  const pendingTotal = useMemo(
    () => pendingByStatus.reduce((sum, [, count]) => sum + (count ?? 0), 0),
    [pendingByStatus],
  );

  const pendingSentences = useMemo(
    () =>
      pendingByStatus
        .filter(([, count]) => (count ?? 0) > 0)
        .map(
          ([status, count]) =>
            `${count} map to ${sourceName} controls that ${STATUS_PHRASE[status] ?? `are marked ${formatStatus(status).toLowerCase()}`}.`,
        ),
    [pendingByStatus, sourceName],
  );


  /* Read from the top level, with the summary as a fallback, and rounded so a
     fractional score does not print a long decimal in the card. */
  const rawTargetScore =
    result?.targetCatalogComplianceScore ??
    summary?.targetCatalogComplianceScore;
  const targetComplianceScore =
    typeof rawTargetScore === "number" && Number.isFinite(rawTargetScore)
      ? Math.round(rawTargetScore)
      : undefined;

  const coveredCount = summary?.implementedCommonControls ?? 0;
  const gapTotal = pendingTotal;
  const netNewCount = summary?.totalTargetOnlyControls ?? 0;
  const targetTotal =
    result?.totalTargetControls ?? coveredCount + gapTotal + netNewCount;
  const share = (value: number) =>
    targetTotal > 0 ? Math.round((value / targetTotal) * 100) : 0;

  const readinessSlices = useMemo(
    () => [
      {
        name: "Implemented",
        value: coveredCount,
        color: COVERED_COLOR,
      },
      ...pendingByStatus.map(([status, count]) => ({
        name: formatStatus(status),
        value: count ?? 0,

        color: STATUS_COLOR[status] ?? GAP_COLOR,
      })),
      {
        name: "Implementation Needed",
        value: netNewCount,
        color: NET_NEW_COLOR,
      },
    ],
    [coveredCount, pendingByStatus, netNewCount],
  );


  const groupedByStatus = useMemo(() => {
    const out: Record<string, GroupedMatchRow[]> = {};
    Object.entries(result?.matchesByStatus ?? {}).forEach(([status, rows]) => {
      const byTarget = new Map<string, GroupedMatchRow>();
      (rows ?? []).forEach((m) => {
        const target = m?.targetControlId ?? m?.controlId ?? "-";
        const source = m?.sourceControlId;
        const existing = byTarget.get(target);
        if (existing) {
          if (source && !existing.sourceControlIds.includes(source)) {
            existing.sourceControlIds.push(source);
          }
        } else {
          byTarget.set(target, {
            targetControlId: target,
            status: m?.sourceImplementationStatus ?? status,
            sourceControlIds: source ? [source] : [],
          });
        }
      });
      out[status] = Array.from(byTarget.values());
    });
    return out;
  }, [result]);

  const groupedColumns: Column<GroupedMatchRow>[] = [
    {
      key: "targetControlId",
      header: "Target Control",
      align: "left",
      minWidth: 130,
      cellClassName: "whitespace-nowrap",
      render: (r) => (
        <span className="whitespace-nowrap">{r?.targetControlId ?? "-"}</span>
      ),
    },
    {
      key: "sourceControlIds",
      header: "Mapped Source Controls",
      align: "left",
      ellipsis: 2,
      tooltip: (r) => r?.sourceControlIds?.join(", "),
      render: (r) =>
        r?.sourceControlIds?.length ? r.sourceControlIds.join(", ") : "-",
    },
    {
      key: "status",
      header: "Status",
      align: "left",

      minWidth: 140,
      cellClassName: "whitespace-nowrap",
      render: (r) => (
        <span className="whitespace-nowrap">
          {r?.status ? formatStatus(r.status) : "-"}
        </span>
      ),
    },
  ];

  const targetOnlyColumns: Column<CatalogComparisonMatch>[] = [
    {
      key: "controlId",
      header: "Target Control",
      align: "left",
      minWidth: 130,
      cellClassName: "whitespace-nowrap",
      render: (r) => (
        <span className="whitespace-nowrap">
          {r?.controlId ?? r?.targetControlId ?? "-"}
        </span>
      ),
    },
  ];

  const coveredColumns = groupedColumns.filter((c) => c.key !== "status");

  const coveredRows = groupedByStatus["implemented"] ?? [];


  const gapRows = useMemo(() => {
    const entries = Object.entries(groupedByStatus).filter(
      ([status]) => status !== "implemented",
    );
    const picked = gapStatus
      ? entries.filter(([status]) => status === gapStatus)
      : entries;
    return picked.flatMap(([, rows]) => rows);
  }, [groupedByStatus, gapStatus]);

  return (
    <div className="px-5 py-4 space-y-4">
      {/* Section header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#475569] dark:text-white/60 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          >
            <ArrowLeft className="size-3.5" />
            Back to Details
          </button>
        </div>
      </div>

      {/* Source, target and the action share one container. */}
      <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.07] bg-white dark:bg-white/[0.02] p-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-0 space-y-1">
          <p className="text-[10px] font-bold tracking-[0.14em] text-[#64748b] dark:text-white/50">
            Source Catalog
          </p>

          <div className="flex items-center gap-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] bg-[#f5f7fb] dark:bg-white/[0.04] px-3 py-2 min-w-0">
            <ShieldCheck className="size-4 flex-shrink-0 text-indigo-600 dark:text-indigo-400" />

            <Tip
              label={sourceName}
              side="top"
              align="start"
              contentClassName="max-w-[400px]"
              className="min-w-0 flex-1"
            >
              <span className="block min-w-0 truncate whitespace-nowrap text-[13px] font-semibold text-[#0f172a] dark:text-white">
                {sourceName}
              </span>
            </Tip>
          </div>
        </div>
        <ArrowRight className="mb-2.5 size-4 flex-shrink-0 text-indigo-500 dark:text-indigo-400" />
        <div className="flex-1 min-w-[180px] space-y-1">
          <p className="text-[10px] font-bold tracking-[0.14em] text-[#64748b] dark:text-white/50">
            Targeted Catalog
          </p>
          <Dropdown
            value={catalogId}
            onChange={(v) => {
              setCatalogId(v);
              setResult(null);
              setAiSummary(null);
              setAiError(null);
            }}
            options={options}
            placeholder="Select Catalog"
            variant="form"
            searchable
            loading={loadingCatalogs}
          />
        </div>
        <button
          type="button"
          onClick={handleCompare}
          disabled={!catalogId || comparing}
          className="flex-shrink-0 inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-45 disabled:hover:bg-indigo-600 disabled:cursor-not-allowed px-4 py-2.5 text-[12px] font-semibold text-white transition-colors"
        >
          <GitCompare className="size-3.5" />
          {comparing ? "Comparing" : "Compare"}
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-xl border border-red-200 dark:border-red-400/25 bg-red-50 dark:bg-red-500/10 px-4 py-3">
          <AlertCircle className="size-4 mt-0.5 flex-shrink-0 text-red-600 dark:text-red-400" />
          <p className="text-[12px] text-red-700 dark:text-red-300 break-words">
            {error}
          </p>
        </div>
      )}

      {comparing ? (
        <section className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.07] bg-white dark:bg-white/[0.02] px-5 py-8 flex items-center justify-center gap-2">
          <Loader2 className="size-4 animate-spin text-indigo-500" />
          <p className="text-[12px] text-[#64748b] dark:text-white/55">
            Running the comparison
          </p>
        </section>
      ) : !result ? (
        <section className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.07] bg-white dark:bg-white/[0.02] px-5 py-8 flex flex-col items-center text-center gap-2">
          <div className="size-10 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center">
            <GitCompare className="size-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <p className="text-[13px] font-bold text-[#0f172a] dark:text-white">
            Select a target catalog to get started
          </p>
          <p className="max-w-sm text-[11px] text-[#64748b] dark:text-white/55">
            Choose a target regulatory catalog and run the comparison to see
            common controls, target-specific controls and the source catalog's
            readiness for the target framework.
          </p>
        </section>
      ) : (
        <>

          {typeof targetComplianceScore === "number" && (
            <TargetScoreCard
              score={targetComplianceScore}
              catalogName={catalogLabel || "Target catalog"}
              subtitle={`Compared against ${sourceName}`}
            />
          )}

          <section className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.07] bg-white dark:bg-white/[0.02] p-4">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="w-full sm:w-[270px] flex-shrink-0">
                <DonutPieChart
                  id="catalog-readiness-donut"
                  name="Target Framework Readiness"
                  data={readinessSlices}
                  height={250}
                  sort="none"
                  maxSlices={readinessSlices.length}
                  showLegend={false}
                  showDataTable={false}
                  showWarnings={false}
                  centerLabel="Overall Coverage"
                  centerValue={
                    <span className="text-[34px] font-bold leading-none tracking-tight text-[#0f172a] dark:text-white">
                      {`${share(coveredCount)}%`}
                    </span>
                  }
                  ariaLabel="Donut chart: target framework readiness"
                />
              </div>

              <div className="flex-1 min-w-0 space-y-3">
                <div>
                  <p className="text-[10px] font-bold tracking-[0.14em] text-[#64748b] dark:text-white/50">
                    Readiness
                  </p>
                  <p className="mt-1 text-[13px] font-semibold text-[#0f172a] dark:text-white break-words">
                    {catalogLabel || "Target catalog"}
                  </p>
                  <p className="mt-1 text-[12px] text-[#64748b] dark:text-white/55 break-words">
                    {[
                      `${coveredCount} of ${targetTotal} target controls are already covered by ${sourceName}.`,
                      ...pendingSentences,
                      `${netNewCount} have no mapping at all.`,
                    ].join(" ")}
                  </p>
                </div>


                <div className="space-y-1.5">
                  {readinessSlices.map((slice) => (
                    <BarKey
                      key={slice.name}
                      color={slice.color}
                      label={slice.name}
                      value={slice.value}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>

          <AiSummaryCard
            loading={aiLoading}
            error={aiError}
            data={aiSummary}
          />

          <OutcomeSection
            tone="emerald"
            icon={<Check className="size-4" />}
            title="Already covered"
            count={coveredCount}
            share={share(coveredCount)}
            description={`${sourceName} already satisfies an equivalent requirement for these target controls. No further work needed.`}
            open={openSection === "covered"}
            onToggle={() =>
              setOpenSection(openSection === "covered" ? null : "covered")
            }
            empty={coveredRows.length === 0}
          >
            <DataTable<GroupedMatchRow>
              key="covered"
              columns={coveredColumns}
              data={coveredRows}
              rowKey={(r, i) => r?.targetControlId ?? String(i)}
              frameless
              compact
              maxHeight="300px"
              emptyText="No covered controls"
            />
          </OutcomeSection>

          <OutcomeSection
            tone="amber"
            icon={<Clock className="size-4" />}
            title="Within reach"
            count={gapTotal}
            share={share(gapTotal)}
            description="These target controls map to source controls that are not implemented yet. Finishing that work closes them."
            open={openSection === "gap"}
            onToggle={() => setOpenSection(openSection === "gap" ? null : "gap")}
            empty={gapRows.length === 0}
            filters={
              pendingByStatus.length > 1 ? (
                <div className="flex flex-wrap gap-1.5">
                  <StatusChip
                    active={gapStatus === null}
                    label="All"
                    count={gapTotal}
                    onClick={() => setGapStatus(null)}
                  />
                  {pendingByStatus.map(([status, count]) => (
                    <StatusChip
                      key={status}
                      active={gapStatus === status}
                      label={formatStatus(status)}
                      count={count ?? 0}
                      color={STATUS_COLOR[status] ?? FALLBACK_STATUS_COLOR}
                      onClick={() => setGapStatus(status)}
                    />
                  ))}
                </div>
              ) : undefined
            }
          >
            <DataTable<GroupedMatchRow>
              key={`gap-${gapStatus ?? "all"}`}
              columns={groupedColumns}
              data={gapRows}
              rowKey={(r, i) => r?.targetControlId ?? String(i)}
              frameless
              compact
              maxHeight="300px"
              emptyText="No controls in this group"
            />
          </OutcomeSection>

          <OutcomeSection
            tone="violet"
            icon={<Plus className="size-4" />}
            title="Needs new implementation"
            count={netNewCount}
            share={share(netNewCount)}
            description={`These target controls have no equivalent in ${sourceName} and have to be built from scratch.`}
            open={openSection === "new"}
            onToggle={() => setOpenSection(openSection === "new" ? null : "new")}
            empty={(result.unmappedTargetControls?.length ?? 0) === 0}
          >
            <DataTable<CatalogComparisonMatch>
              key="target-only"
              columns={targetOnlyColumns}
              data={result.unmappedTargetControls ?? []}
              rowKey={(r, i) => r?.controlId ?? String(i)}
              frameless
              compact
              maxHeight="300px"
              emptyText="No controls in this group"
            />
          </OutcomeSection>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-xl border border-[#e2e8f0] dark:border-white/[0.07] bg-[#f8fafc] dark:bg-white/[0.02] px-4 py-3">
            <ScopeStat
              icon={<ShieldCheck className="size-3.5" />}
              label="Controls in source catalog"
              value={result.totalSourceControls ?? 0}
            />
            <ScopeStat
              icon={<FileText className="size-3.5" />}
              label="Controls in target catalog"
              value={targetTotal}
            />
            <ScopeStat
              icon={<Link2 className="size-3.5" />}
              label="Mapped in common"
              value={summary?.totalCommonControls ?? 0}
            />
          </div>
        </>
      )}
    </div>
  );
}
